import React, { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import memberService from '../../services/memberService';
import { useTheme } from '@mui/material/styles';
import api from '../../services/api';
// Import Material UI components
import {
  Box, Grid, Paper, Typography, TextField, Button, 
  Avatar, IconButton, Alert, CircularProgress, 
  Tabs, Tab, Divider, Card, CardContent, Stack, Chip,
  Tooltip, Popover, InputAdornment, Dialog, DialogActions, DialogContent, 
  DialogContentText, DialogTitle, Menu, MenuItem, ListItemIcon, ListItemText, List, ListItem
} from '@mui/material';
// Import Material UI icons
import {
  PhotoCamera as PhotoCameraIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Lock as LockIcon,
  CheckCircleOutline as CheckCircleIcon,
  CardMembership as CardMembershipIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarMonth as CalendarMonthIcon,
  LocationOn as LocationOnIcon,
  Palette as PaletteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  QrCode as QrCodeIcon,
  Print as PrintIcon,
  ContentCopy as ContentCopyIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { Link } from 'react-router-dom';
// Import html-to-image and QR code library
import QRCode from 'react-qr-code';
import * as htmlToImage from 'html-to-image';

const DEFAULT_AVATAR_URL = 'https://ui-avatars.com/api/?name=User&background=0D8ABC&color=fff';

// Define color themes
const colorThemes = {
  default: {
    name: 'Default Blue',
    gradient: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
    accent: '#3f51b5',
    accentLight: 'rgba(63, 81, 181, 0.2)'
  },
  emerald: {
    name: 'Emerald Green',
    gradient: 'linear-gradient(135deg, #2E7D32 0%, #43A047 100%)',
    accent: '#2E7D32',
    accentLight: 'rgba(46, 125, 50, 0.2)'
  },
  sunset: {
    name: 'Sunset Orange',
    gradient: 'linear-gradient(135deg, #E64A19 0%, #FF7043 100%)',
    accent: '#E64A19',
    accentLight: 'rgba(230, 74, 25, 0.2)'
  },
  royal: {
    name: 'Royal Purple',
    gradient: 'linear-gradient(135deg, #512DA8 0%, #7E57C2 100%)',
    accent: '#512DA8',
    accentLight: 'rgba(81, 45, 168, 0.2)'
  },
  midnight: {
    name: 'Midnight Blue',
    gradient: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
    accent: '#1A237E',
    accentLight: 'rgba(26, 35, 126, 0.2)'
  }
};

const MemberProfilePage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('profile');
  
  // Profile picture state
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState(DEFAULT_AVATAR_URL);
  const [uploadingPicture, setUploadingPicture] = useState(false);
  const [memberProfile, setMemberProfile] = useState(null);
  const fileInputRef = useRef(null);

  // Membership card configuration
  const [showMembershipCard, setShowMembershipCard] = useState(true);
  
  // Color theme state
  const [selectedTheme, setSelectedTheme] = useState(() => {
    return localStorage.getItem('memberCardTheme') || 'default';
  });
  const [colorMenuAnchor, setColorMenuAnchor] = useState(null);

  // Confirmation dialog state
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Add state and refs for membership card functionality
  const [qrValue, setQrValue] = useState('');
  const [showQrCode, setShowQrCode] = useState(true); // Show QR code by default
  const [cardThemeMenuAnchor, setCardThemeMenuAnchor] = useState(null);
  const [cardActionMenuAnchor, setCardActionMenuAnchor] = useState(null);
  const membershipCardRef = useRef(null);

  // Profile form
  const {
    register: registerProfile,
    handleSubmit: handleSubmitProfile,
    formState: { errors: profileErrors },
    reset: resetProfile
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      email: user?.email || ''
    }
  });

  // Password form
  const {
    register: registerPassword,
    handleSubmit: handleSubmitPassword,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }
  });

  const newPassword = watch('newPassword', '');

  // Load member profile and profile picture
  useEffect(() => {
    const loadMemberProfile = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // First check if we have an auth token
        const token = localStorage.getItem('token');
        if (!token) {
          console.error('No authentication token found');
          setError('Authentication required. Please login again.');
          setIsLoading(false);
          return;
        }
        
        // Attempt to load the member profile through both endpoints for redundancy
        let profile = null;
        let memberError = null;
        
        try {
          console.log('Attempting to load member profile from primary endpoint...');
          const response = await memberService.getCurrentMemberProfile();
          if (response.data && response.data.data) {
            profile = response.data.data;
            console.log('Successfully loaded profile from primary endpoint');
          }
        } catch (error) {
          console.error('Error fetching member profile:', error);
          memberError = error;
          // We'll try the fallback endpoint before giving up
        }
        
        // If primary endpoint failed, try fallback endpoint
        if (!profile) {
          try {
            console.log('Attempting to load user profile from fallback endpoint...');
            const userResponse = await api.get('/auth/current-user');
            if (userResponse.data && userResponse.data.data) {
              profile = {
                ...userResponse.data.data,
                // If we only have user data, create a minimal member profile
                userId: userResponse.data.data._id || userResponse.data.data.id
              };
              console.log('Successfully loaded profile from fallback endpoint');
            }
          } catch (error) {
            console.error('Both profile endpoints failed:', error);
            // Both endpoints failed, use the first error
            if (!memberError) memberError = error;
          }
        }
        
        if (profile) {
          setMemberProfile(profile);
          
          // Set profile picture if available
          if (profile.profilePicture) {
            setProfilePicture(profile.profilePicture);
            const pictureUrl = constructProfilePictureUrl(profile.profilePicture);
            console.log("Setting profile picture URL:", pictureUrl);
            setProfilePictureUrl(pictureUrl);
            
            const img = new Image();
            img.src = pictureUrl;
            img.onload = () => console.log("Profile image pre-loaded successfully");
            img.onerror = (e) => console.error("Failed to pre-load profile image:", e);
          }
        } else {
          console.error('Failed to load member profile:', memberError);
          if (memberError?.response?.status === 401) {
            setError('Your session has expired. Please login again.');
          } else {
            setError('Failed to load your profile. Please try refreshing the page.');
          }
        }
      } catch (error) {
        console.error('Failed to load member profile:', error);
        setError('Something went wrong. Please refresh the page and try again.');
      } finally {   
        setIsLoading(false);
      }
    };
    loadMemberProfile();
  }, []);

  // Add these to your existing useEffect that loads profile data
  useEffect(() => {
    // Generate QR code value with member information
    if (memberProfile) {
      const qrData = {
        memberId: memberProfile.memberId || memberProfile._id,
        name: `${user?.firstName} ${user?.lastName}`,
        email: user?.email,
        memberSince: memberProfile?.joinDate,
        expiryDate: memberProfile?.membershipExpiryDate,
        membershipType: memberProfile?.membershipType,
        organization: memberProfile?.organization || memberProfile?.tenant?.name || 'Member Organization'
      };
      setQrValue(JSON.stringify(qrData));
    }
  }, [memberProfile, user]);

  // Load saved theme from localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem('memberCardTheme');
    if (savedTheme && colorThemes[savedTheme]) {
      setSelectedTheme(savedTheme);
    }
  }, []);

  // Format membership expiry date
  const formatMembershipExpiry = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Format join date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  // Get membership status and color
  const getMembershipStatus = () => {
    if (memberProfile?.status) {
      const status = memberProfile.status.toLowerCase();
      
      if (status === 'active') {
        return { status: 'Active', color: 'success' };
      } else if (status === 'pending') {
        return { status: 'Pending', color: 'warning' };
      } else if (status === 'expired') {
        return { status: 'Expired', color: 'error' };
      } else if (status === 'suspended') {
        return { status: 'Suspended', color: 'error' };
      }
    }
    
    if (memberProfile?.membershipExpiryDate) {
      const expiryDate = new Date(memberProfile.membershipExpiryDate);
      const today = new Date();
      
      if (expiryDate < today) {
        return { status: 'Expired', color: 'error' };
      }

      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(today.getDate() + 30);
      
      if (expiryDate < thirtyDaysFromNow) {
        return { status: 'Expiring Soon', color: 'warning' };
      }
       
      return { status: 'Active', color: 'success' };
    }

    if (memberProfile?.joinDate) {
      return { status: 'Active', color: 'success' };
    }
    
    return { status: 'Active', color: 'default' };
  };

  const constructProfilePictureUrl = (profilePicturePath) => {
    if (!profilePicturePath) return DEFAULT_AVATAR_URL;
    
    try {
      // If it's already a full URL, return it
      if (profilePicturePath.startsWith('http')) {
        return profilePicturePath;
      }
      
      // Get base URL from environment or API defaults
      let baseUrl = import.meta.env.VITE_API_URL || '';
      if (!baseUrl && api.defaults.baseURL) {
        baseUrl = api.defaults.baseURL;
      }
      
      // Default fallback URL
      if (!baseUrl) baseUrl = 'http://localhost:5000';
      
      // Remove /api suffix if present for image URLs
      baseUrl = baseUrl.endsWith('/api') ? baseUrl.slice(0, -4) : baseUrl;
      
      // Remove trailing slash if present
      baseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
      
      // Log the base URL for debugging
      console.log('Base URL for profile picture:', baseUrl);
      
      // Ensure the path starts with a slash
      let normalizedPath = profilePicturePath;
      if (!normalizedPath.startsWith('/')) {
        normalizedPath = '/' + normalizedPath;
      }
      
      // Handle specific path formats
      if (normalizedPath.includes('/uploads/profile-pictures/')) {
        // Path already contains the full uploads directory structure
        return `${baseUrl}${normalizedPath}`;
      } else if (normalizedPath.includes('/uploads/')) {
        // Path contains uploads but not the specific subfolder
        return `${baseUrl}${normalizedPath}`;
      } else {
        // Add the uploads/profile-pictures path
        normalizedPath = normalizedPath.replace(/^\//, ''); // Remove leading slash if any
        return `${baseUrl}/uploads/profile-pictures/${normalizedPath}`;
      }
    } catch (e) {
      console.error('Error constructing profile picture URL:', e);
      return DEFAULT_AVATAR_URL;
    }
  };

  const getProfilePictureUrl = () => {
    return profilePictureUrl;
  };

  // Handle profile update
  const onProfileSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      
      // Check authentication before submitting
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }
      
      // Use memberService instead of context
      const response = await memberService.updateCurrentMemberProfile(data);
      
      // Update user data in multiple locations for consistency
      if (response && response.data && response.data.success) {
        try {
          // Update firstName and lastName in the auth context if available
          if (user) {
            // Create updated user object with new name data
            const updatedUser = { 
              ...user, 
              firstName: data.firstName, 
              lastName: data.lastName 
            };
            
            // Update localStorage with new user data for auth persistence
            localStorage.setItem('authUser', JSON.stringify(updatedUser));
            
            // Also update 'user' item if it exists (some components may use this)
            const userStr = localStorage.getItem('user');
            if (userStr) {
              try {
                const userObj = JSON.parse(userStr);
                userObj.firstName = data.firstName;
                userObj.lastName = data.lastName;
                localStorage.setItem('user', JSON.stringify(userObj));
              } catch (e) {
                console.warn('Error updating user in localStorage:', e);
              }
            }
          }
        } catch (storageError) {
          console.warn('Error updating local storage after profile update:', storageError);
          // Continue with success flow even if local storage update failed
        }
      }
      
      setSuccess('Profile updated successfully');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      
      // Add more detailed error handling for auth errors
      if (error.response?.status === 401) {
        setError('Your session has expired. Please login again to continue.');
      } else {
        setError(error.response?.data?.message || 'Failed to update profile');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      setSuccess(null);
      await memberService.changePassword(data.currentPassword, data.newPassword);
      setSuccess('Password changed successfully');
      resetPassword();
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      if (error.response?.data?.message?.includes('does not have a password set')) {
        setError(
          <Box>
            <Typography gutterBottom>
              Your account needs a password reset. Please use the "Forgot Password" feature on the login page.
            </Typography>
            <Button 
              component={Link}
              to="/auth/forgot-password" 
              variant="contained" 
              color="primary" 
              size="small"
              sx={{ mt: 1 }}
            >
              Go to Forgot Password
            </Button>
          </Box>
        );
      } else {
        setError(error.response?.data?.message || 'Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      console.error('No file selected');
      return;
    }
    
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file'); 
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size cannot exceed 5MB'); 
      return;
    }
    
    try {
      setUploadingPicture(true);
      setError(null);
      setSuccess(null);
      
      // Get authentication token
      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Authentication required. Please login again.');
        setUploadingPicture(false);
        return;
      }
      
      // Create the local preview before upload
      const localPreviewUrl = URL.createObjectURL(file);
      setProfilePictureUrl(localPreviewUrl);
      
      // Create a new FormData instance for each upload attempt
      const formData = new FormData();
      
      // Add the file with the exact field name expected by multer
      formData.append('profilePicture', file);
      
      // Check if FormData contains the file (logging)
      let formDataLog = [];
      for (let pair of formData.entries()) {
        formDataLog.push({
          name: pair[0],
          type: pair[1] instanceof File ? 'File' : typeof pair[1],
          fileName: pair[1] instanceof File ? pair[1].name : null,
          size: pair[1] instanceof File ? pair[1].size : null
        });
      }
      console.log('FormData contents:', formDataLog);
      
      // Get base URL for API
      const baseUrl = api.defaults.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
      const url = `${baseUrl}/members/profile/picture`;
      
      console.log('Uploading profile picture to:', url);
      
      // Set the required headers - don't set Content-Type for multipart/form-data
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Add tenant ID if available
      const tenantId = localStorage.getItem('tenantId');
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId;
      }
      
      // Add user ID if available
      const userId = localStorage.getItem('userId');
      if (userId) {
        headers['X-User-ID'] = userId;
      }
      
      console.log('Starting upload with headers:', {
        auth: 'Bearer Token Present',
        tenantId: tenantId || 'Not found',
        userId: userId || 'Not found'
      });
      
      // Use axios directly with the minimal configuration needed for proper file upload
      const response = await api.post(url, formData, {
        headers: headers,
        timeout: 60000 // 60 seconds timeout
      });
      
      console.log('Upload response received:', response.status);
      
      if (response && response.data && response.data.success) {
        const newProfilePicture = response.data.data.profilePicture;
        setProfilePicture(newProfilePicture);
        
        console.log('Profile picture updated to:', newProfilePicture);
        
        // Get the full URL for the new profile picture
        const serverUrl = constructProfilePictureUrl(newProfilePicture);
        
        // Release the object URL to prevent memory leaks
        URL.revokeObjectURL(localPreviewUrl);
        
        // Test loading the image from server
        const img = new Image();
        img.src = serverUrl;
        
        // Handle successful image load
        img.onload = () => {
          console.log("Server profile image loaded successfully");
          setProfilePictureUrl(serverUrl);
          setSuccess('Profile picture updated successfully');
          
          setTimeout(() => {
            setSuccess(null);
          }, 3000);
        };
        
        // Handle image load error
        img.onerror = (e) => {
          console.error("Failed to load server profile image:", e);
          // Keep using the local preview URL if server image fails to load
          setProfilePictureUrl(localPreviewUrl);
          setSuccess('Profile picture uploaded but there may be a delay before it appears');
        };
      } else {
        console.error('Invalid server response:', response.data);
        throw new Error('Server returned an invalid response');
      }
    } catch (error) {
      console.error('Error uploading profile picture:', error);
      
      // Get detailed error information
      if (error.response) {
        console.error('Server error details:', {
          status: error.response.status,
          data: error.response.data
        });
        
        if (error.response.status === 401) {
          setError('Authentication required to upload profile picture. Please login again.');
        } else if (error.response.status === 413) {
          setError('Image size too large. Please select a smaller image file.');
        } else if (error.response.status === 400) {
          setError(error.response.data?.message || 'Invalid request. Please try a different image.');
        } else {
          setError(error.response.data?.message || `Server error (${error.response.status})`);
        }
      } else if (error.request) {
        console.error('No response received from server');
        setError('Network error. Please check your connection and try again.');
      } else {
        console.error('Error during upload setup:', error.message);
        setError(error.message || 'Failed to upload profile picture');
      }
      
      // Fallback to previous picture or default
      if (profilePicture) {
        setProfilePictureUrl(constructProfilePictureUrl(profilePicture));
      } else {
        setProfilePictureUrl(DEFAULT_AVATAR_URL);
      }
    } finally {
      setUploadingPicture(false);
    }
  };

  const openDeleteConfirmDialog = () => {
    if (!profilePicture) return;
    setConfirmDialogOpen(true);
  };

  const closeDeleteConfirmDialog = () => {
    setConfirmDialogOpen(false);
  };

  const handleDeleteProfilePicture = async () => {
    if (!profilePicture) return;
    
    try {
      setUploadingPicture(true);
      setError(null);
      setSuccess(null);
      setConfirmDialogOpen(false);
      
      const response = await memberService.deleteProfilePicture();
      
      if (response && response.success) {
        setProfilePicture(null);
        setProfilePictureUrl(DEFAULT_AVATAR_URL);
        setSuccess('Profile picture removed successfully');
        
        setTimeout(() => {
          setSuccess(null);
        }, 3000);
      }
    } catch (error) {
      console.error('Error deleting profile picture:', error);
      setError(error.response?.data?.message || 'Failed to delete profile picture');
    } finally {
      setUploadingPicture(false);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const toggleMembershipCard = () => {
    setShowMembershipCard(!showMembershipCard);
  };

  const handleThemeChange = (themeKey) => {
    setSelectedTheme(themeKey);
    localStorage.setItem('memberCardTheme', themeKey);
    setColorMenuAnchor(null);
  };

  // Function to handle copying membership ID to clipboard
  const handleCopyMembershipId = () => {
    const membershipId = memberProfile?.membershipNumber || memberProfile?.memberId || user?.id;
    if (membershipId) {
      navigator.clipboard.writeText(membershipId)
        .then(() => {
          setSuccess('Membership ID copied to clipboard');
          setTimeout(() => {
            setSuccess(null);
          }, 3000);
        })
        .catch(err => {
          console.error('Failed to copy membership ID:', err);
          setError('Failed to copy membership ID');
        });
    }
  };

  // Function to download the membership card as an image
  const handleDownloadCard = () => {
    if (!membershipCardRef.current) return;
    
    try {
      setCardActionMenuAnchor(null);
      
      // Use specific options to ensure gradients and background colors are properly captured
      const options = {
        quality: 1.0,
        pixelRatio: 3,
        skipAutoScale: true,
        cacheBust: true,
        style: {
          'transform': 'scale(1)',
          'transform-origin': 'top left',
          'background': colorThemes[selectedTheme].gradient,
        },
        filter: (node) => {
          // Keep all nodes for proper rendering
          return true;
        }
      };
      
      // Add a temporary background class to ensure the colors show up
      membershipCardRef.current.classList.add('download-prepare');
      
      // First create a temporary transparent version for layering
      htmlToImage.toPng(membershipCardRef.current, options)
        .then(function (dataUrl) {
          // Create download link
          const link = document.createElement('a');
          link.download = `${user?.firstName || 'Member'}-${user?.lastName || ''}-membership-card.png`;
          link.href = dataUrl;
          link.click();
          
          // Remove temporary class
          membershipCardRef.current.classList.remove('download-prepare');
          
          setSuccess('Membership card downloaded successfully');
          setTimeout(() => {
            setSuccess(null);
          }, 3000);
        })
        .catch(function (error) {
          console.error('Error generating card image:', error);
          membershipCardRef.current.classList.remove('download-prepare');
          setError('Failed to download card: ' + error.message);
        });
    } catch (err) {
      console.error('Error downloading membership card:', err);
      if (membershipCardRef.current) {
        membershipCardRef.current.classList.remove('download-prepare');
      }
      setError('Failed to download membership card: ' + err.message);
    }
  };

  // Function to print the membership card
  const handlePrintCard = () => {
    setCardActionMenuAnchor(null);
    
    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        setError('Please allow pop-ups to print your membership card');
        return;
      }
      
      const cardElement = membershipCardRef.current;
      if (!cardElement) {
        setError('Card element not found');
        return;
      }
      
      // Get membership ID - important for the card
      const membershipId = memberProfile?.membershipNumber || memberProfile?.memberId || user?.id || 'N/A';
      
      // Create print template with enhanced security features - portrait mode
      const cardHtml = `
        <!DOCTYPE html>
        <html>
          <head>
            <title>Membership Card - ${user?.firstName} ${user?.lastName}</title>
            <meta charset="utf-8">
            <style>
              @page {
                size: 85.6mm 53.98mm;
                margin: 0;
                color-adjust: exact;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              body {
                margin: 0;
                padding: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                min-height: 100vh;
                font-family: 'Segoe UI', Arial, sans-serif;
                background-color: #f0f0f0;
                color-adjust: exact;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .print-container {
                padding: 0;
                box-sizing: border-box;
                width: 100%;
                display: flex;
                justify-content: center;
                align-items: center;
              }
              
              .card-container {
                width: 85.6mm;
                height: 53.98mm;
                position: relative;
                border-radius: 6px;
                background: ${colorThemes[selectedTheme].gradient};
                background-color: ${colorThemes[selectedTheme].accent}; /* Fallback */
                color: white;
                box-sizing: border-box;
                overflow: hidden;
                border: 1px solid rgba(0,0,0,0.1);
                color-adjust: exact;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              
              .security-background {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                opacity: 0.08;
                background-image: repeating-linear-gradient(45deg, rgba(255,255,255,0.3) 0px, rgba(255,255,255,0.3) 2px, transparent 2px, transparent 4px);
                z-index: 1;
              }
              
              .card-content {
                padding: 12px;
                height: 100%;
                position: relative;
                z-index: 2;
                display: flex;
                flex-direction: column;
                box-sizing: border-box;
              }
              
              .pattern-overlay {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='rgba(255,255,255,0.1)' fill-rule='evenodd'/%3E%3C/svg%3E");
                background-size: 150px;
                background-repeat: repeat;
                z-index: 1;
                opacity: 0.2;
              }
              
              .watermark {
                position: absolute;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 1;
                opacity: 0.07;
                font-size: 90px;
                font-weight: 900;
                transform: rotate(-30deg);
                text-transform: uppercase;
                pointer-events: none;
              }
              
              .hologram {
                position: absolute;
                width: 40px;
                height: 40px;
                top: 12px;
                right: 12px;
                background: linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.1));
                border-radius: 50%;
                z-index: 5;
                display: flex;
                align-items: center;
                justify-content: center;
                overflow: hidden;
                border: 1px solid rgba(255,255,255,0.4);
              }
              
              .hologram::before {
                content: "";
                position: absolute;
                width: 200%;
                height: 200%;
                background: radial-gradient(circle, rgba(255,255,255,0.3) 0%, rgba(255,255,255,0) 70%);
              }
              
              .hologram::after {
                content: "${memberProfile?.organization?.substring(0, 1) || 'M'}";
                font-size: 20px;
                font-weight: bold;
                color: rgba(255,255,255,0.9);
                position: relative;
                z-index: 2;
              }
              
              .microtext {
                position: absolute;
                bottom: 2px;
                left: 0;
                right: 0;
                text-align: center;
                font-size: 3px;
                opacity: 0.6;
                letter-spacing: 0.5px;
                text-transform: uppercase;
                z-index: 10;
              }
              
              .header-row {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 8px;
              }
              
              .logo-section {
                display: flex;
                align-items: center;
              }
              
              .logo-icon {
                margin-right: 4px;
                font-size: 14px;
              }
              
              .organization-name {
                font-size: 11px;
                font-weight: bold;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                max-width: 130px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              
              .profile-row {
                display: flex;
                margin-bottom: 8px;
              }
              
              .avatar {
                width: 45px;
                height: 45px;
                border-radius: 50%;
                overflow: hidden;
                margin-right: 8px;
                border: 2px solid rgba(255,255,255,0.3);
                background-color: rgba(255,255,255,0.2);
                flex-shrink: 0;
              }
              
              .avatar img {
                width: 100%;
                height: 100%;
                object-fit: cover;
              }
              
              .member-info {
                flex: 1;
                min-width: 0;
              }
              
              .member-name {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 2px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              
              .member-type {
                display: inline-block;
                background-color: rgba(255,255,255,0.2);
                padding: 1px 5px;
                border-radius: 9px;
                font-size: 8px;
                font-weight: bold;
                text-transform: uppercase;
              }
              
              .details-row {
                display: flex;
                flex-wrap: wrap;
                margin-bottom: 5px;
              }
              
              .detail-item {
                width: 50%;
                margin-bottom: 3px;
                padding-right: 4px;
                box-sizing: border-box;
              }
              
              .detail-label {
                font-size: 7px;
                opacity: 0.7;
                text-transform: uppercase;
                margin-bottom: 1px;
              }
              
              .detail-value {
                font-size: 10px;
                font-weight: bold;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              }
              
              .id-section {
                display: flex;
                flex-direction: column;
                margin-top: 3px;
                padding-top: 5px;
                border-top: 1px solid rgba(255,255,255,0.2);
              }
              
              .membership-id-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
              }
              
              .id-column {
                max-width: 65%;
              }
              
              .id-label {
                font-size: 7px;
                opacity: 0.7;
                text-transform: uppercase;
                margin-bottom: 1px;
              }
              
              .id-value {
                font-family: 'Courier New', monospace;
                font-size: 11px;
                font-weight: bold;
                letter-spacing: 0.5px;
              }
              
              .qr-section {
                width: 46px;
                height: 46px;
                background-color: white;
                border-radius: 6px;
                overflow: hidden;
                display: flex;
                flex-direction: column;
                align-items: center;
                box-shadow: 0 2px 4px rgba(0,0,0,0.15);
                flex-shrink: 0;
              }
              
              .qr-text {
                width: 100%;
                text-align: center;
                font-size: 4px;
                color: #333;
                padding: 1px 0;
                font-weight: bold;
                text-transform: uppercase;
              }
              
              .security-stripe {
                position: absolute;
                height: 4px;
                left: 0;
                right: 0;
                background: linear-gradient(90deg, 
                  rgba(255,255,255,0.5) 0%, 
                  rgba(255,255,255,0.8) 20%, 
                  rgba(255,255,255,0.5) 40%, 
                  rgba(255,255,255,0.8) 60%, 
                  rgba(255,255,255,0.5) 80%, 
                  rgba(255,255,255,0.8) 100%);
                bottom: 10px;
                z-index: 3;
                opacity: 0.7;
              }
              
              .diagonal-pattern {
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                top: 0;
                background-image: repeating-linear-gradient(45deg, 
                  rgba(255,255,255,0) 0px, 
                  rgba(255,255,255,0) 10px, 
                  rgba(255,255,255,0.1) 10px, 
                  rgba(255,255,255,0.1) 12px);
                z-index: 1;
                pointer-events: none;
              }
              
              @media print {
                body { 
                  background: none; 
                  -webkit-print-color-adjust: exact !important;
                  color-adjust: exact !important;
                  print-color-adjust: exact !important;
                }
                .print-container { 
                  padding: 0; 
                }
                .card-container { 
                  box-shadow: none;
                  border: 1px solid #ddd;
                }
                .qr-section {
                  box-shadow: none;
                  border: 1px solid rgba(0,0,0,0.1);
                }
                @page { 
                  size: 85.6mm 53.98mm;
                  margin: 0; 
                }
              }
            </style>
          </head>
          <body>
            <div class="print-container">
              <div class="card-container">
                <div class="pattern-overlay"></div>
                <div class="diagonal-pattern"></div>
                <div class="watermark">${memberProfile?.organization || 'Verified'}</div>
                <div class="hologram"></div>
                <div class="security-stripe"></div>
                <div class="security-background"></div>
                
                <div class="card-content">
                  <div class="header-row">
                    <div class="logo-section">
                      <span class="logo-icon">🔒</span>
                      <div class="organization-name">${memberProfile?.organization || 'MEMBERSHIP CARD'}</div>
                    </div>
                  </div>
                  
                  <div class="profile-row">
                    <div class="avatar">
                      <img src="${getProfilePictureUrl()}" alt="${user?.firstName || 'User'}'s profile" onerror="this.src='${DEFAULT_AVATAR_URL}'">
                    </div>
                    <div class="member-info">
                      <div class="member-name">${user?.firstName} ${user?.lastName}</div>
                      <div class="member-type">${memberProfile?.membershipType || 'STANDARD'}</div>
                    </div>
                  </div>
                  
                  <!-- Move this section up to prevent cutting off at bottom -->
                  <div class="id-section">
                    <div class="membership-id-row">
                      <div class="id-column">
                        <div class="id-label">Membership ID</div>
                        <div class="id-value">${membershipId}</div>
                      </div>
                      <div class="qr-section">
                        <div class="qr-text">Scan to verify</div>
                        <div style="width:100%; height:42px; box-sizing:border-box; background-color:white;">
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 37 37" style="width:100%; height:100%; shape-rendering:crispEdges;">
                            <path fill="#FFFFFF" d="M0 0h37v37H0z"></path>
                            <path fill="#000000" d="M4 4h1v1H4zm1 0h1v1H5zm1 0h1v1H6zm1 0h1v1H7zm1 0h1v1H8zm1 0h1v1H9zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm3 0h1v1h-1zm2 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm3 0h1v1h-1zm1 0h1v1h-1zM4 5h1v1H4zm6 0h1v1h-1zm4 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm5 0h1v1h-1zm6 0h1v1h-1zM4 6h1v1H4zm2 0h1v1H6zm1 0h1v1H7zm1 0h1v1H8zm2 0h1v1h-1zm4 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm4 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zM4 7h1v1H4zm2 0h1v1H6zm1 0h1v1H7zm1 0h1v1H8zm2 0h1v1h-1zm2 0h1v1h-1zm3 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm3 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zM4 8h1v1H4zm2 0h1v1H6zm1 0h1v1H7zm1 0h1v1H8zm2 0h1v1h-1zm3 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm3 0h1v1h-1zm4 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm3 0h1v1h-1zM4 9h1v1H4zm6 0h1v1h-1zm2 0h1v1h-1zm2 0h1v1h-1zm2 0h1v1h-1zm3 0h1v1h-1zm4 0h1v1h-1zm1 0h1v1h-1zm6 0h1v1h-1zM4 10h1v1H4zm1 0h1v1H5zm1 0h1v1H6zm1 0h1v1H7zm1 0h1v1H8zm1 0h1v1H9zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm3 0h1v1h-1zm1 0h1v1h-1zm2 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1zm1 0h1v1h-1z"></path>
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div class="details-row">
                    <div class="detail-item">
                      <div class="detail-label">Member Since</div>
                      <div class="detail-value">${formatDate(memberProfile?.joinDate)}</div>
                    </div>
                    <div class="detail-item">
                      <div class="detail-label">Status</div>
                      <div class="detail-value">${getMembershipStatus().status}</div>
                    </div>
                    <div class="detail-item">
                      <div class="detail-label">Expiry Date</div>
                      <div class="detail-value">${formatMembershipExpiry(memberProfile?.membershipExpiryDate)}</div>
                    </div>
                    <div class="detail-item">
                      <div class="detail-label">Verification</div>
                      <div class="detail-value">Scan QR ↗</div>
                    </div>
                  </div>
                </div>
                <div class="microtext">SECURITY FEATURES: HOLOGRAM • MICROTEXT • SECURITY STRIPE • DIAGONAL PATTERN • WATERMARK • QR VERIFICATION</div>
              </div>
            </div>
            <script>
              window.onload = function() {
                // Force rendering of all elements before printing
                setTimeout(function() { 
                  window.print();
                  setTimeout(function() { 
                    window.close(); 
                  }, 1000);
                }, 300);
              }
            </script>
          </body>
        </html>
      `;
      
      printWindow.document.open();
      printWindow.document.write(cardHtml);
      printWindow.document.close();
      
      setSuccess('Printing membership card...');
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
    } catch (err) {
      console.error('Error printing membership card:', err);
      setError('Failed to print membership card: ' + err.message);
    }
  };

  const renderMembershipCard = () => {
    const membershipStatus = getMembershipStatus();
    
    return (
      <Paper 
        elevation={3} 
        sx={{ 
          mb: 4, 
          borderRadius: 3,
          overflow: 'hidden',
          background: colorThemes[selectedTheme].gradient,
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg"%3E%3Cpath d="M0 0h100v100H0z" fill="none"/%3E%3Cpath d="M0 0h100v100H0z" fill="rgba(255,255,255,0.05)"/%3E%3C/svg%3E")',
            opacity: 0.1,
            zIndex: 0,
          },
          transition: 'transform 0.3s ease, box-shadow 0.3s ease',
          '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
          }
        }}
        ref={membershipCardRef}
      >
        <Box sx={{ p: 4, position: 'relative', zIndex: 1 }}>
          {/* Decorative elements */}
          <Box 
            sx={{
              position: 'absolute',
              right: 24,
              top: 24,
              width: 60,
              height: 40,
              opacity: 0.7,
              background: 'radial-gradient(circle at 0 0, transparent 40%, rgba(255,255,255,0.1) 40%)',
              borderRadius: '8px',
            }}
          />
          <Box 
            sx={{
              position: 'absolute',
              left: 24,
              bottom: 24,
              width: 100,
              height: 40,
              opacity: 0.3,
              background: 'radial-gradient(circle at 100% 100%, transparent 40%, rgba(255,255,255,0.2) 40%)',
              borderRadius: '8px',
            }}
          />
          
          {/* Card Header */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CardMembershipIcon sx={{ color: 'white', fontSize: 28 }} />
              <Typography 
                variant="h5" 
                sx={{ 
                  color: 'white',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 1,
                }}
              >
                {memberProfile?.organization || 'MEMBERSHIP CARD'}
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Card actions">
                <IconButton
                  onClick={(e) => setCardActionMenuAnchor(e.currentTarget)}
                  sx={{ color: 'white' }}
                  size="small"
                >
                  <MoreVertIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Change card theme">
                <IconButton
                  onClick={(e) => setColorMenuAnchor(e.currentTarget)}
                  sx={{ color: 'white' }}
                  size="small"
                >
                  <PaletteIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Action menu popover */}
          <Menu
            anchorEl={cardActionMenuAnchor}
            open={Boolean(cardActionMenuAnchor)}
            onClose={() => setCardActionMenuAnchor(null)}
            PaperProps={{
              sx: {
                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                borderRadius: 2,
                minWidth: 180
              }
            }}
          >
            <MenuItem onClick={handleDownloadCard}>
              <ListItemIcon>
                <DownloadIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Download" />
            </MenuItem>
            <MenuItem onClick={handlePrintCard}>
              <ListItemIcon>
                <PrintIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Print" />
            </MenuItem>
            <MenuItem onClick={() => { setShowQrCode(!showQrCode); setCardActionMenuAnchor(null); }}>
              <ListItemIcon>
                <QrCodeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={showQrCode ? "Hide QR Code" : "Show QR Code"} />
            </MenuItem>
            <MenuItem onClick={handleCopyMembershipId}>
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Copy ID" />
            </MenuItem>
          </Menu>
          
          {/* Color theme menu popover */}
          <Popover
            open={Boolean(colorMenuAnchor)}
            anchorEl={colorMenuAnchor}
            onClose={() => setColorMenuAnchor(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                overflow: 'hidden'
              }
            }}
          >
            <Box sx={{ p: 1 }}>
              {Object.entries(colorThemes).map(([key, theme]) => (
                <Button
                  key={key}
                  fullWidth
                  onClick={() => handleThemeChange(key)}
                  sx={{
                    justifyContent: 'flex-start',
                    px: 2,
                    py: 1,
                    mb: 0.5,
                    background: theme.gradient,
                    color: 'white',
                    '&:hover': {
                      background: theme.gradient,
                      opacity: 0.9,
                    }
                  }}
                >
                  {theme.name}
                </Button>
              ))}
            </Box>
          </Popover>
          
          {/* Card Content - Two Columns Layout */}
          <Grid container spacing={4}>
            {/* Left Column - Member Info */}
            <Grid item xs={12} md={showQrCode ? 8 : 12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  src={getProfilePictureUrl()}
                  alt={`${user?.firstName || 'User'}'s profile`}
                  sx={{ 
                    width: 100, 
                    height: 100, 
                    mr: 3,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    fontSize: '2rem',
                    border: '4px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
                  }}
                >
                  {user?.firstName?.[0]}{user?.lastName?.[0]}
                </Avatar>
                <Box>
                  <Typography variant="h4" sx={{ color: 'white', mb: 1, fontWeight: 'medium' }}>
                    {user?.firstName} {user?.lastName}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <EmailIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 16 }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                      {user?.email}
                    </Typography>
                  </Box>
                  <Chip 
                    label={memberProfile?.membershipType?.toUpperCase() || 'STANDARD'} 
                    size="small"
                    sx={{ 
                      bgcolor: colorThemes[selectedTheme].accentLight,
                      color: 'white',
                      fontWeight: 'bold',
                      backdropFilter: 'blur(4px)'
                    }} 
                  />
                </Box>
              </Box>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CalendarMonthIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 16 }} />
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Member Since
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {formatDate(memberProfile?.joinDate)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CheckCircleIcon sx={{ 
                        color: 
                          membershipStatus.color === 'success' ? '#4caf50' : 
                          membershipStatus.color === 'warning' ? '#ff9800' : 
                          membershipStatus.color === 'error' ? '#f44336' : 'rgba(255,255,255,0.7)', 
                        mr: 1, 
                        fontSize: 16 
                      }} />
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Status
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ 
                      color: 'white', 
                      textTransform: 'capitalize',
                    }}>
                      {membershipStatus.status}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                      <CalendarMonthIcon sx={{ color: 'rgba(255,255,255,0.7)', mr: 1, fontSize: 16 }} />
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                        Expiry Date
                      </Typography>
                    </Box>
                    <Typography variant="h6" sx={{ color: 'white' }}>
                      {formatMembershipExpiry(memberProfile?.membershipExpiryDate)}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
            
            {/* Right Column - QR Code (conditionally rendered) */}
            {showQrCode && (
              <Grid item xs={12} md={4} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Box 
                  sx={{ 
                    bgcolor: 'white', 
                    p: 2, 
                    borderRadius: 2, 
                    boxShadow: '0 4px 14px rgba(0,0,0,0.15)',
                    textAlign: 'center',
                    width: '100%'
                  }}
                >
                  <Typography variant="caption" color="text.secondary" gutterBottom>
                    Scan to verify membership
                  </Typography>
                  <Box sx={{ p: 1, bgcolor: 'white' }}>
                    {/* Actual QR Code using react-qr-code */}
                    <QRCode
                      value={qrValue}
                      size={200}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      level="M"
                      fgColor="#000000"
                      bgColor="#FFFFFF"
                    />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, fontSize: '0.7rem' }}>
                    ID: {memberProfile?.membershipNumber || memberProfile?.memberId || user?.id || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
          
          {/* Card Footer */}
          <Box sx={{ 
            mt: 'auto', 
            pt: 3, 
            borderTop: '1px solid rgba(255,255,255,0.2)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end'
          }}>
            <Box>
              <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.7)', display: 'block', mb: 0.5 }}>
                Membership ID
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: 'white',
                    fontFamily: 'monospace',
                    letterSpacing: 2,
                    mr: 1
                  }}
                >
                  {memberProfile?.membershipNumber || memberProfile?.memberId || user?.id || 'N/A'}
                </Typography>
                <Tooltip title="Copy ID to clipboard">
                  <IconButton 
                    size="small" 
                    sx={{ color: 'rgba(255,255,255,0.7)' }}
                    onClick={handleCopyMembershipId}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleDownloadCard}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Download
              </Button>
              <Button
                variant="contained"
                size="small"
                startIcon={<PrintIcon />}
                onClick={handlePrintCard}
                sx={{ 
                  bgcolor: 'rgba(255,255,255,0.2)', 
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  '&:hover': {
                    bgcolor: 'rgba(255,255,255,0.3)',
                  }
                }}
              >
                Print
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    );
  };

  return (
    <Box sx={{ bgcolor: '#f5f7fa', minHeight: '100vh', p: 3 }}>
      <Paper 
        elevation={0} 
        sx={{ 
          mb: 4, 
          p: 3,  
          borderRadius: 2,
          background: 'linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%)',
          color: 'white',
        }}
      >
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 'medium', display: 'flex', alignItems: 'center' }}>
          <PersonIcon sx={{ mr: 1 }} /> Your Profile
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.8 }}>
          Manage your account information and password
        </Typography>
      </Paper>
      {success && (
        <Alert 
          icon={<CheckCircleIcon fontSize="inherit" />} 
          severity="success" 
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {success}
        </Alert>
      )}
      {error && (
        <Alert 
          severity="error"
          sx={{ mb: 3, borderRadius: 2 }}
        >
          {error}
        </Alert>
      )}
      {showMembershipCard && (
        <Grid container spacing={4}>
          {/* Left Column - Membership Card */}
          <Grid item xs={12} md={6}>
            {renderMembershipCard()}
          </Grid>
          
          {/* Right Column - Member Benefits */}
          <Grid item xs={12} md={6}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: 2,
                overflow: 'hidden',
                borderLeft: '4px solid #4CAF50',
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)',
                height: '100%'
              }}
            >
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 3, 
                  fontWeight: 'medium', 
                  display: 'flex', 
                  alignItems: 'center',
                  color: '#4CAF50' 
                }}
              >
                <CardMembershipIcon sx={{ mr: 1 }} /> Membership Benefits
              </Typography>
              
              <List sx={{ mb: 2 }}>
                <ListItem alignItems="flex-start">
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Exclusive Member Events"
                    secondary="Access to special networking opportunities and member-only events"
                  />
                </ListItem>
                
                <ListItem alignItems="flex-start">
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Request Assistance"
                    secondary="Full access to request assistance, materials and resources"
                  />
                </ListItem>
                
                <ListItem alignItems="flex-start">
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Community Support"
                    secondary="Connect with other members through our community forums and groups"
                  />
                </ListItem>
                
                
                
                <ListItem alignItems="flex-start">
                  <ListItemIcon>
                    <CheckCircleIcon color="success" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Member Directory Listing"
                    secondary="Your profile will be listed in our member directory for networking"
                  />
                </ListItem>
              </List>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <Chip 
                  label={memberProfile?.membershipType?.toUpperCase() || 'STANDARD'} 
                  color="success" 
                  size="large"
                  sx={{ 
                    fontSize: '1rem', 
                    py: 2.5, 
                    fontWeight: 'bold',
                    boxShadow: '0 4px 8px rgba(76, 175, 80, 0.15)'
                  }} 
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
      <Dialog
        open={confirmDialogOpen}
        onClose={closeDeleteConfirmDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{
          sx: {
            borderRadius: 2,
            borderTop: '4px solid #f44336',
            maxWidth: 400,
            width: '100%'
          }
        }}
      >
        <DialogTitle id="alert-dialog-title" sx={{ pb: 1 }}>
          <Box display="flex" alignItems="center" gap={1}>
            <WarningIcon color="error" />
            <Typography variant="h6" component="span">
              Remove Profile Picture
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description" sx={{ mb: 1 }}>
            Are you sure you want to remove your profile picture? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button 
            onClick={closeDeleteConfirmDialog}
            color="primary"
            variant="outlined"
            sx={{ borderRadius: 1.5 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteProfilePicture}
            color="error"
            variant="contained"
            startIcon={<DeleteIcon />}
            autoFocus
            sx={{ 
              borderRadius: 1.5,
              boxShadow: '0 4px 6px rgba(244, 67, 54, 0.25)',
              '&:hover': {
                boxShadow: '0 6px 10px rgba(244, 67, 54, 0.3)',
              }
            }}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
      <Paper 
        elevation={2}
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          borderLeft: '4px solid #2196F3',
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(31, 38, 135, 0.15)'
        }}
      >
        <Tabs 
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ 
            bgcolor: 'rgba(33,150,243,0.03)',
            borderBottom: '1px solid rgba(0,0,0,0.08)',
            '& .MuiTab-root': { py: 2 },
            '& .Mui-selected': { 
              color: '#2196F3',
              fontWeight: 'medium'
            },
          }}
          TabIndicatorProps={{
            style: { backgroundColor: '#2196F3' }
          }}
        >
          <Tab 
            value="profile" 
            label="Profile Information" 
            icon={<PersonIcon sx={{ mb: 0.5 }} />} 
            iconPosition="start"
          />
          <Tab 
            value="password" 
            label="Change Password" 
            icon={<LockIcon sx={{ mb: 0.5 }} />} 
            iconPosition="start"
          />
        </Tabs>
        <Box sx={{ p: 3 }}>
          {activeTab === 'profile' && (
            <Box component="form" onSubmit={handleSubmitProfile(onProfileSubmit)}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      background: 'linear-gradient(to right bottom, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: '#2196F3',
                      mb: 3
                    }}>
                      <PersonIcon sx={{ mr: 1 }} /> Personal Information
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="First name"
                      variant="outlined"
                      sx={{ mb: 3 }}
                      {...registerProfile('firstName', { required: 'First name is required' })}
                      error={!!profileErrors.firstName}
                      helperText={profileErrors.firstName?.message}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    <TextField
                      fullWidth
                      label="Last name"
                      variant="outlined"
                      sx={{ mb: 3 }}
                      {...registerProfile('lastName', { required: 'Last name is required' })}
                      error={!!profileErrors.lastName}
                      helperText={profileErrors.lastName?.message}
                      disabled
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PersonIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      background: 'linear-gradient(to right bottom, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: '#2196F3',
                      mb: 3
                    }}>
                      <EmailIcon sx={{ mr: 1 }} /> Contact Information
                    </Typography>
                    
                    <TextField
                      fullWidth
                      label="Email address"
                      variant="outlined"
                      sx={{ mb: 3 }}
                      disabled
                      {...registerProfile('email')}
                      helperText="Email address cannot be changed"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="primary" />
                          </InputAdornment>
                        ),
                      }}
                    />
                    
                    <Alert 
                      severity="info" 
                      variant="outlined"
                      icon={<EmailIcon />}
                      sx={{ 
                        borderRadius: 2,
                        mt: 2
                      }}
                    >
                      To change your email address, please contact support.
                    </Alert>
                  </Paper>
                </Grid>
                
                <Grid item xs={12} md={4}>
                  <Paper
                    elevation={1}
                    sx={{
                      p: 3,
                      height: '100%',
                      borderRadius: 2,
                      background: 'linear-gradient(to right bottom, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.7))',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      boxShadow: '0 8px 32px rgba(31, 38, 135, 0.1)'
                    }}
                  >
                    <Typography variant="h6" gutterBottom sx={{ 
                      display: 'flex', 
                      alignItems: 'center',
                      color: '#2196F3', 
                      mb: 3
                    }}>
                      <CardMembershipIcon sx={{ mr: 1 }} /> Membership Info
                    </Typography>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Membership Type
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Chip 
                          label={memberProfile?.membershipType?.toUpperCase() || 'STANDARD'} 
                          size="small"
                          color="primary"
                          sx={{ mr: 1, fontWeight: 'bold' }} 
                        />
                      </Typography>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Profile Picture
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          startIcon={<PhotoCameraIcon />}
                          onClick={triggerFileInput}
                          disabled={uploadingPicture}
                          size="small"
                        >
                          {uploadingPicture ? 'Uploading...' : profilePicture ? 'Change' : 'Upload'}
                        </Button>
                        {profilePicture && (
                          <Button
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={openDeleteConfirmDialog}
                            disabled={uploadingPicture}
                            size="small"
                          >
                            Remove
                          </Button>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          onChange={handleFileChange}
                          accept="image/*"
                        />
                      </Box>
                    </Box>
                    
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="caption" color="text.secondary">
                        Membership Status
                      </Typography>
                      <Typography variant="h6" sx={{ 
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <Chip 
                          label={getMembershipStatus().status.toUpperCase()} 
                          size="small"
                          color={
                            getMembershipStatus().color === 'success' ? 'success' : 
                            getMembershipStatus().color === 'warning' ? 'warning' : 
                            getMembershipStatus().color === 'error' ? 'error' : 'default'
                          }
                          sx={{ mr: 1, fontWeight: 'bold' }} 
                        />
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    {/* Save button removed as first name and last name fields are disabled */}
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          {activeTab === 'password' && (
            <Box component="form" onSubmit={handleSubmitPassword(onPasswordSubmit)} sx={{ '& .MuiTextField-root': { mb: 3 } }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Current password"
                    variant="outlined"
                    {...registerPassword('currentPassword', { required: 'Current password is required' })}
                    error={!!passwordErrors.currentPassword}
                    helperText={passwordErrors.currentPassword?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="New password"
                    variant="outlined"
                    {...registerPassword('newPassword', {
                      required: 'New password is required',
                      minLength: {
                        value: 8,
                        message: 'Password must be at least 8 characters'
                      }
                    })}
                    error={!!passwordErrors.newPassword}
                    helperText={passwordErrors.newPassword?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    type="password"
                    label="Confirm new password"
                    variant="outlined"
                    {...registerPassword('confirmPassword', {
                      required: 'Please confirm your new password',
                      validate: value => value === newPassword || 'Passwords do not match'
                    })}
                    error={!!passwordErrors.confirmPassword}
                    helperText={passwordErrors.confirmPassword?.message}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Alert severity="info" sx={{ mb: 3 }}>
                    <Typography variant="body2">
                      If you can't remember your current password or don't have one set, please use the 
                      <Link 
                        to="/auth/forgot-password" 
                        style={{ marginLeft: '5px', marginRight: '5px', fontWeight: 'bold' }}
                      >
                        Forgot Password
                      </Link> 
                      feature to reset it.
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      type="submit"
                      variant="contained"
                      disabled={isLoading}
                      sx={{
                        background: 'linear-gradient(45deg, #2196F3 30%, #64B5F6 90%)',
                        boxShadow: '0 4px 6px rgba(33, 150, 243, 0.25)',
                        '&:hover': {
                          boxShadow: '0 6px 10px rgba(33, 150, 243, 0.3)'
                        }
                      }}
                    >
                      {isLoading ? 'Changing Password...' : 'Change Password'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default MemberProfilePage;